import type { Cell, PackItem } from "./types";
import { bounding, uniqueRotations } from "./geometry";

/** A candidate placement: one item, one rotation, at one absolute grid spot. */
export interface Placement {
  itemIdx: number;
  value: number;
  /** Absolute [row, col] cells this placement occupies. */
  cells: Cell[];
}

export interface EnumeratedPlacements {
  placements: Placement[];
  /** "r,c" -> indices of placements covering that cell. */
  cellCover: Record<string, number[]>;
  /** itemIdx -> indices of that item's placements. */
  itemPlace: number[][];
}

/** Enumerate every legal (rotation × position) placement of every item. */
export function enumeratePlacements(items: PackItem[], cols: number, rows: number): EnumeratedPlacements {
  const placements: Placement[] = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    for (const rot of uniqueRotations(item.cells)) {
      const { h, w } = bounding(rot);
      for (let r = 0; r <= rows - h; r++) {
        for (let c = 0; c <= cols - w; c++) {
          placements.push({
            itemIdx: i,
            value: item.value,
            cells: rot.map(([dr, dc]): Cell => [r + dr, c + dc]),
          });
        }
      }
    }
  }
  const cellCover: Record<string, number[]> = {};
  for (let p = 0; p < placements.length; p++) {
    for (const [r, c] of placements[p].cells) {
      const k = r + "," + c;
      (cellCover[k] || (cellCover[k] = [])).push(p);
    }
  }
  const itemPlace: number[][] = Array.from({ length: items.length }, () => []);
  for (let p = 0; p < placements.length; p++) itemPlace[placements[p].itemIdx].push(p);
  return { placements, cellCover, itemPlace };
}
